import type { AssetManifest, GachaResultWire, Rarity } from '@kenocyber/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { staticAssetUrl } from '../game/assets';
import {
  ambientMoteCount,
  bestResultIndex,
  hasTransformation,
  isNeutralPhase,
  panelMoteCount,
  particleCount,
  type RevealPhase,
  revealHoldMs,
  revealIntensity,
  revealSeal,
  revealTimeline,
  ringCount,
  shakeAmplitudePx,
  shakeMs,
  shardCount,
} from './gacha-reveal-timeline';
import { rarityLabel, rarityStyle } from './rarity';

/**
 * The reveal: a secret, a rupture and a prize.
 *
 * ## It presents; it never decides
 *
 * Every result was drawn, charged, granted and persisted before this component
 * existed. Skipping moves an index. Closing unmounts a div. Neither calls the
 * server, re-rolls anything, changes the pity counter or returns a coin - and
 * the test suite asserts exactly that rather than trusting the sentence.
 *
 * ## The rarity is a secret for the first 70%
 *
 * The client HAS the answer from the first frame, and the whole job of this
 * file is not to leak it. Before the rupture there is no `data-rarity`, no
 * rarity custom property, no coloured border, no name, no label and no picture
 * of any kind - not even a silhouette, because a ship outline is recognisable
 * and anybody who knows the roster could name the prize from one. What charges
 * instead is a containment vessel that looks identical whatever is inside it.
 * The particle, ring and shard counts are held flat for the same reason: a
 * Prismatic arriving with three times the sparks would announce itself just as
 * loudly as a colour would.
 *
 * ## Everything shares one centre
 *
 * `.gacha-anchor` does the centring and NOTHING else; its child does the scale
 * or the rotation. That split is the fix for the flash sitting right of centre:
 * an animated `transform` on the same element replaces the `translate(-50%,
 * -50%)` that was holding it in place, and the old `inset: 0; margin: auto`
 * silently gave up on any layer wider than its container.
 *
 * ## No canvas, no renderer per card
 *
 * Pets are 3D in the arena and PNG everywhere else. The portal, the rings, the
 * sparks and the shockwaves are CSS transforms and opacity - the two properties
 * a compositor can animate without touching layout.
 */

interface GachaRevealProps {
  readonly manifest: AssetManifest;
  readonly results: readonly GachaResultWire[];
  readonly onClose: () => void;
}

/** Stable keys for the vessel's shards. Named slots, never an array index. */
const SHARD_SLOTS = [
  'shard-a',
  'shard-b',
  'shard-c',
  'shard-d',
  'shard-e',
  'shard-f',
  'shard-g',
  'shard-h',
  'shard-i',
  'shard-j',
  'shard-k',
  'shard-l',
] as const;

/** Stable keys for the ring stack. Four named slots, never an array index. */
const RING_SLOTS = ['ring-inner', 'ring-mid', 'ring-outer', 'ring-halo'] as const;

/** A particle's fixed randomness. Computed once per field, never per frame. */
interface Mote {
  readonly id: string;
  readonly angle: number;
  readonly distance: number;
  readonly delay: number;
  readonly duration: number;
  readonly size: number;
}

function buildMotes(count: number, seed: number): readonly Mote[] {
  /*
   * Deterministic from the index, not `Math.random`.
   *
   * Not for fairness - nothing here touches an outcome - but because a
   * re-render must not reshuffle every spark, and because a test that counts
   * them should not have to tolerate noise.
   */
  return Array.from({ length: count }, (_, index) => {
    const step = (index * 137.508 + seed * 31) % 360;
    return {
      id: `mote-${seed}-${index}`,
      angle: step,
      distance: 34 + ((index * 17 + seed * 7) % 46),
      delay: ((index * 83) % 1100) / 1000,
      duration: 1.3 + ((index * 37) % 110) / 100,
      size: 2 + ((index * 13) % 4),
    };
  });
}

/**
 * The centring layer.
 *
 * One job: put its child's origin on the exact centre of the cinema. It never
 * animates, so no keyframe can overwrite the translate - which is precisely
 * how the final flash drifted right before.
 */
function Anchor({ children }: { children: React.ReactNode }) {
  return <span className="gacha-anchor">{children}</span>;
}

export function GachaReveal({ manifest, results, onClose }: GachaRevealProps) {
  /** Which result is on stage. `results.length` means the summary. */
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState<RevealPhase>('darken');
  /** A result the player clicked in the summary, shown larger. Never equips. */
  const [inspecting, setInspecting] = useState<GachaResultWire | null>(null);

  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const reducedMotion = useMemo(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    [],
  );

  const done = index >= results.length;
  const current = results[index] ?? null;

  /**
   * Cancels everything pending.
   *
   * Stable identity, so the effects below can depend on it honestly instead of
   * closing over a function React recreates every render - which is the shape
   * that lets a stale cleanup miss a timer.
   */
  const clearTimers = useCallback(() => {
    for (const timer of timers.current) {
      clearTimeout(timer);
    }
    timers.current = [];
  }, []);

  /**
   * Plays one result's timeline, then hands over to the next.
   *
   * Re-runs whenever the index changes, and its cleanup cancels the whole chain
   * - so a skip mid-build cannot leave a stray `setTimeout` that fires into an
   * unmounted tree.
   */
  useEffect(() => {
    if (!current) {
      return;
    }
    setPhase('darken');
    let elapsed = 0;
    for (const step of revealTimeline(current.rarity, { reducedMotion })) {
      // Each phase flips at the START of its own slice.
      timers.current.push(setTimeout(() => setPhase(step.phase), elapsed));
      elapsed += step.durationMs;
    }
    /*
     * Then the reading pause, and only then the next roll.
     *
     * A timer of its own on top of the animation rather than the tail of its
     * last phase: a player needs the same time to read a name and a rarity
     * whatever the show cost, and on a Common that tail was worth under a
     * second.
     */
    timers.current.push(
      setTimeout(() => setIndex((value) => value + 1), elapsed + revealHoldMs(current.rarity)),
    );
    return clearTimers;
  }, [current, reducedMotion, clearTimers]);

  useEffect(() => clearTimers, [clearTimers]);

  /**
   * Reveals the CURRENT prize now.
   *
   * Cancels the chain, jumps straight to `reveal`, and schedules only the
   * hand-over to the next result. It does not touch `results`, which came out
   * of a committed transaction.
   */
  const skipCurrent = useCallback(() => {
    if (!current) {
      return;
    }
    clearTimers();
    setPhase('reveal');
    // Skipping the build does not skip the READING: the prize still holds for
    // its full pause before the next roll takes the screen.
    timers.current.push(
      setTimeout(() => setIndex((value) => value + 1), revealHoldMs(current.rarity)),
    );
  }, [clearTimers, current]);

  /** Goes straight to the grid. Same results, no chain left running. */
  const skipAll = useCallback(() => {
    clearTimers();
    setIndex(results.length);
  }, [clearTimers, results.length]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }
      // Escape during the build SKIPS rather than hides: a player must never be
      // able to close their own results away before seeing what they bought.
      event.stopPropagation();
      if (inspecting) {
        setInspecting(null);
      } else if (!done) {
        skipAll();
      } else {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [done, inspecting, onClose, skipAll]);

  /*
   * The panel's own colour state, derived from the SAME flag the stage uses.
   *
   * The frame around the panel, the field of sparks inside it and the shake all
   * hang off this, so they cannot arrive a frame before or after the rarity
   * does.
   */
  const revealed = current !== null && !isNeutralPhase(phase);
  const rarity = revealed ? (current.rarity as Rarity) : null;

  return (
    <div
      className="gacha-reveal"
      data-testid="gacha-reveal"
      data-phase={done ? 'summary' : phase}
      {...(rarity ? { 'data-rarity': rarity } : {})}
      data-intensity={rarity ? revealIntensity(rarity) : 0}
      data-transform={rarity && hasTransformation(rarity) ? 'true' : 'false'}
      data-shake={rarity && shakeMs(rarity, reducedMotion) > 0 ? 'true' : 'false'}
      style={
        rarity
          ? ({
              ...rarityStyle(rarity),
              '--shake-ms': `${shakeMs(rarity, reducedMotion)}ms`,
              '--shake-amp': `${shakeAmplitudePx(rarity)}px`,
            } as React.CSSProperties)
          : undefined
      }
    >
      {/* The neon frame on the panel, mirroring the one on the screen edge. */}
      {rarity ? (
        <>
          <span className="gacha-panel-frame" data-testid="gacha-panel-frame" aria-hidden="true" />
          <span className="gacha-panel-runner" aria-hidden="true" />
          <PanelMotes rarity={rarity} reducedMotion={reducedMotion} />
        </>
      ) : null}
      {current ? (
        <Stage
          manifest={manifest}
          result={current}
          phase={phase}
          reducedMotion={reducedMotion}
          position={index + 1}
          total={results.length}
        />
      ) : (
        <Summary manifest={manifest} results={results} onInspect={setInspecting} />
      )}

      {/*
        A fixed strip at the foot of the reveal. Anchored to the panel rather
        than to the stage, so the controls never move between rarities - a
        button that shifts under the cursor is a button that gets missed.
      */}
      <div className="gacha-reveal-actions">
        {done ? (
          <button
            type="button"
            className="neon-button"
            data-testid="gacha-reveal-close"
            onClick={onClose}
          >
            FECHAR
          </button>
        ) : (
          <>
            <button
              type="button"
              className="gacha-skip-button"
              data-testid="gacha-skip"
              // Enter and Space come free on a real <button>; nothing here
              // reimplements them badly.
              onClick={skipCurrent}
            >
              PULAR ANIMAÇÃO
            </button>
            {results.length > 1 ? (
              <button
                type="button"
                className="gacha-skip-button gacha-skip-all"
                data-testid="gacha-skip-all"
                onClick={skipAll}
              >
                PULAR TUDO
              </button>
            ) : null}
          </>
        )}
      </div>

      {/* A closer look at one card. Looking is all it does. */}
      {inspecting ? (
        <div
          className="gacha-inspect"
          data-testid="gacha-inspect"
          role="dialog"
          style={rarityStyle(inspecting.rarity)}
        >
          <Art manifest={manifest} result={inspecting} size="inspect" />
          <span className="gacha-showcase-name">{inspecting.name}</span>
          <span className="market-row-sub">
            {rarityLabel(inspecting.rarity)} · {inspecting.categoryLabel} · {inspecting.levelLabel}
            {inspecting.stars ? ` · ${'★'.repeat(inspecting.stars)}` : ''}
          </span>
          <button
            type="button"
            className="ghost-button"
            data-testid="gacha-inspect-close"
            onClick={() => setInspecting(null)}
          >
            FECHAR
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The sparks that fill the PANEL.
 *
 * The densest of the three fields, because the panel is the frame the player is
 * actually looking at. Like the other two it exists only after the rupture, so
 * it can scale with the rarity without giving anything away.
 */
function PanelMotes({ rarity, reducedMotion }: { rarity: Rarity; reducedMotion: boolean }) {
  const motes = useMemo(
    () => buildAmbientMotes(panelMoteCount(rarity, reducedMotion)),
    [rarity, reducedMotion],
  );
  return (
    <span className="gacha-panel-motes" data-testid="gacha-panel-motes" aria-hidden="true">
      {motes.map((mote) => (
        <span
          key={mote.id}
          className="gacha-panel-mote"
          style={
            {
              left: `${mote.left}%`,
              top: `${mote.top}%`,
              '--drift': `${mote.drift}px`,
              '--mote-delay': `${mote.delay}s`,
              '--mote-duration': `${mote.duration}s`,
              '--mote-size': `${mote.size}px`,
            } as React.CSSProperties
          }
        />
      ))}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/*  The whole screen                                                          */
/* -------------------------------------------------------------------------- */

/** One drifting spark, placed anywhere on the viewport. Computed once. */
interface AmbientMote {
  readonly id: string;
  readonly left: number;
  readonly top: number;
  readonly drift: number;
  readonly delay: number;
  readonly duration: number;
  readonly size: number;
}

function buildAmbientMotes(count: number): readonly AmbientMote[] {
  // Deterministic, like the portal's own field: a re-render must not reshuffle
  // the sky, and a test that counts them should not have to tolerate noise.
  return Array.from({ length: count }, (_, index) => ({
    id: `ambient-${index}`,
    left: (index * 37.6) % 100,
    top: (index * 61.3) % 100,
    drift: -60 - ((index * 23) % 90),
    delay: ((index * 137) % 2600) / 1000,
    duration: 3.4 + ((index * 53) % 240) / 100,
    size: 2 + ((index * 11) % 5),
  }));
}

/**
 * The rest of the screen, once the colour has landed.
 *
 * ## Why it is a portal
 *
 * `.gacha-panel` carries `transform: translateX(-50%)`, and a transformed
 * ancestor makes `position: fixed` resolve against THAT element rather than the
 * viewport. A fixed child of the panel would therefore be trapped inside the
 * panel - which is exactly the box this layer exists to escape. Rendering into
 * `document.body` is the only way to reach the real screen.
 *
 * ## Why it only exists after the rupture
 *
 * It is mounted by the caller, and only from `rupture` onward. Nothing about
 * the rarity may reach the screen before then, and a full-viewport wash in the
 * rarity's own colour is about as loud a leak as there could be. Mounting
 * rather than hiding also means there is no invisible particle field running
 * during the suspense.
 *
 * ## Why it cannot be clicked
 *
 * `pointer-events: none` throughout. It covers the arena, the HUD and the skip
 * buttons, and every one of those has to keep working while it is up.
 */
function GachaAmbient({ rarity, reducedMotion }: { rarity: Rarity; reducedMotion: boolean }) {
  const motes = useMemo(
    () => buildAmbientMotes(ambientMoteCount(rarity, reducedMotion)),
    [rarity, reducedMotion],
  );

  return createPortal(
    <div
      className="gacha-ambient"
      data-testid="gacha-ambient"
      data-rarity={rarity}
      data-intensity={revealIntensity(rarity)}
      data-transform={hasTransformation(rarity) ? 'true' : 'false'}
      data-shake={shakeMs(rarity, reducedMotion) > 0 ? 'true' : 'false'}
      style={
        {
          ...rarityStyle(rarity),
          '--shake-ms': `${shakeMs(rarity, reducedMotion)}ms`,
          '--shake-amp': `${shakeAmplitudePx(rarity)}px`,
        } as React.CSSProperties
      }
      aria-hidden="true"
    >
      {/* A wash that is transparent in the middle: the prize has to stay the
          most readable thing on screen. */}
      <span className="gacha-ambient-wash" />
      {/* The neon frame, and a runner that travels around it. */}
      <span className="gacha-ambient-frame" />
      <span className="gacha-ambient-runner" />
      <span className="gacha-ambient-motes">
        {motes.map((mote) => (
          <span
            key={mote.id}
            className="gacha-ambient-mote"
            style={
              {
                left: `${mote.left}%`,
                top: `${mote.top}%`,
                '--drift': `${mote.drift}px`,
                '--mote-delay': `${mote.delay}s`,
                '--mote-duration': `${mote.duration}s`,
                '--mote-size': `${mote.size}px`,
              } as React.CSSProperties
            }
          />
        ))}
      </span>
    </div>,
    document.body,
  );
}

/* -------------------------------------------------------------------------- */
/*  One reveal                                                                */
/* -------------------------------------------------------------------------- */

function Stage({
  manifest,
  result,
  phase,
  reducedMotion,
  position,
  total,
}: {
  manifest: AssetManifest;
  result: GachaResultWire;
  phase: RevealPhase;
  reducedMotion: boolean;
  position: number;
  total: number;
}) {
  /*
   * The secret, enforced in ONE place.
   *
   * `neutral` is true for the first 70%, and every branch below reads it. The
   * rarity stays in the props the whole time - the timeline needs it to know
   * how long to run - but nothing derived from it reaches the DOM until this
   * flips.
   */
  const neutral = isNeutralPhase(phase);
  const intense = !neutral;
  const rings = ringCount(result.rarity, intense);
  const motes = useMemo(
    () => buildMotes(particleCount(result.rarity, { intense, reducedMotion }), position),
    [result.rarity, intense, reducedMotion, position],
  );
  const seal = revealSeal(result.rarity);
  const shards = shardCount(result.rarity, intense);
  /*
   * The item is drawn ONLY once it has been revealed.
   *
   * There is no silhouette, and that was not a stylistic call: a ship outline
   * is recognisable, so anybody who knows the roster could name the prize
   * before the portal broke. What charges in its place is a containment vessel,
   * which looks the same whatever is inside it.
   */
  const showArt = phase === 'reveal';

  return (
    <div
      className="gacha-stage"
      data-testid="gacha-stage"
      data-phase={phase}
      // Withheld until the rupture. The attribute IS the leak: a stylesheet
      // keyed on it would paint Prismatic's rainbow over a silhouette.
      {...(neutral ? {} : { 'data-rarity': result.rarity })}
      data-intensity={neutral ? 0 : revealIntensity(result.rarity)}
      data-transform={!neutral && hasTransformation(result.rarity) ? 'true' : 'false'}
      // No `data-shake` here on purpose: the tremor moves the whole overlay and
      // the whole screen, not the stage inside them. Shaking a child of a shaking
      // parent doubles the displacement on exactly the two loudest rarities.
      // The rarity custom properties travel with the attribute, not before it.
      style={neutral ? undefined : rarityStyle(result.rarity)}
    >
      {/*
        The rest of the screen, and only from the rupture on.

        Mounted by the SAME `neutral` flag that unlocks the colour here, so the
        wash, the frame and the drifting field all arrive on the exact frame the
        rarity does - one condition, not three that could drift apart.
      */}
      {neutral ? null : (
        <GachaAmbient rarity={result.rarity as Rarity} reducedMotion={reducedMotion} />
      )}

      {/*
        The cinema: a square, position: relative, and the single origin every
        layer below centres on. Not the panel, not the window - the centre of
        THIS box is the centre of the animation.
      */}
      <div className="gacha-cinema" data-testid="gacha-cinema">
        <Anchor>
          <span className="gacha-core" aria-hidden="true" />
        </Anchor>

        {RING_SLOTS.slice(0, rings).map((slot, ring) => (
          <Anchor key={slot}>
            {/* The anchor centres; this child rotates. Two elements, because
                one cannot do both without the keyframe winning. */}
            <span className="gacha-ring" data-ring={ring} aria-hidden="true" />
          </Anchor>
        ))}

        <Anchor>
          <span className="gacha-vortex" aria-hidden="true" />
        </Anchor>
        <Anchor>
          <span className="gacha-wave" aria-hidden="true" />
        </Anchor>
        <Anchor>
          <span className="gacha-wave gacha-wave-late" aria-hidden="true" />
        </Anchor>

        {/* Sparks, positioned from the same centre as everything else. */}
        <Anchor>
          <span className="gacha-motes" data-testid="gacha-motes" aria-hidden="true">
            {motes.map((mote) => (
              <span
                key={mote.id}
                className="gacha-mote"
                style={
                  {
                    '--mote-angle': `${mote.angle}deg`,
                    '--mote-distance': `${mote.distance}%`,
                    '--mote-delay': `${mote.delay}s`,
                    '--mote-duration': `${mote.duration}s`,
                    '--mote-size': `${mote.size}px`,
                  } as React.CSSProperties
                }
              />
            ))}
          </span>
        </Anchor>

        {/* Mythic and above, and only after the rupture has already happened. */}
        {!neutral && hasTransformation(result.rarity) ? (
          <Anchor>
            <span className="gacha-transform" data-testid="gacha-transform" aria-hidden="true">
              <span className="gacha-fracture" />
              <span className="gacha-beams" />
              <span className="gacha-glyphs" />
            </span>
          </Anchor>
        ) : null}

        {/* The burst. Its own anchor, so the brightest thing on screen is
            geometrically centred whatever it scales to. */}
        <Anchor>
          <span className="gacha-flash" data-testid="gacha-flash" aria-hidden="true" />
        </Anchor>

        {/*
          What replaced the silhouette: a vessel charging around something the
          player cannot see. Its shard count is FLAT while the secret holds - a
          vessel that grew with the rarity would be the same leak in a new
          shape.
        */}
        {showArt ? null : (
          <Anchor>
            <span className="gacha-vessel" data-testid="gacha-vessel" aria-hidden="true">
              <span className="gacha-vessel-core" />
              {SHARD_SLOTS.slice(0, shards).map((slot, shard) => (
                <span
                  key={slot}
                  className="gacha-shard"
                  style={{ '--shard-angle': `${(360 / shards) * shard}deg` } as React.CSSProperties}
                />
              ))}
              <span className="gacha-vessel-cage" />
            </span>
          </Anchor>
        )}

        <Anchor>
          <div className="gacha-showcase" data-testid="gacha-showcase">
            {showArt ? <Art manifest={manifest} result={result} size="stage" /> : null}
          </div>
        </Anchor>
      </div>

      {/*
        The caption, outside the cinema and below it - so a long item name can
        never push the portal off its own centre.
      */}
      <div className="gacha-caption">
        {phase === 'reveal' ? (
          <>
            <span className="gacha-showcase-name" data-testid="gacha-name">
              {result.name}
            </span>
            <span className="market-row-sub">
              {rarityLabel(result.rarity)} · {result.categoryLabel} · {result.levelLabel}
              {result.stars ? ` · ${'★'.repeat(result.stars)}` : ''}
            </span>
            {result.forcedByPity ? (
              <span className="gacha-forced" data-testid="gacha-forced">
                GARANTIA DE 60 GIROS
              </span>
            ) : null}
          </>
        ) : null}
        {seal && !neutral ? (
          <span className="gacha-seal" data-testid="gacha-seal">
            {seal}
          </span>
        ) : null}
      </div>

      <span className="gacha-progress">
        {position} / {total}
      </span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  The prize, and the grid                                                   */
/* -------------------------------------------------------------------------- */

/**
 * The item itself, and only ever once it has been revealed.
 *
 * There is no silhouette mode any more. The PNG is the same one the inventory
 * draws, at the same 99% under .
 */
function Art({
  manifest,
  result,
  size,
}: {
  manifest: AssetManifest;
  result: GachaResultWire;
  size: 'stage' | 'grid' | 'inspect';
}) {
  const url = result.assetKey ? staticAssetUrl(manifest, result.assetKey) : null;
  return (
    <span
      className={`gacha-art gacha-art-${size}`}
      data-testid={size === 'stage' ? 'gacha-art' : undefined}
    >
      {url ? (
        <img src={url} alt="" draggable={false} decoding="async" />
      ) : (
        <span className="gacha-art-missing" aria-hidden="true" />
      )}
    </span>
  );
}

function Summary({
  manifest,
  results,
  onInspect,
}: {
  manifest: AssetManifest;
  results: readonly GachaResultWire[];
  onInspect: (result: GachaResultWire) => void;
}) {
  // The best of the pull, singled out. Ties keep the first, so the highlight is
  // stable rather than depending on how the array happened to be ordered.
  const best = bestResultIndex(results.map((entry) => entry.rarity as Rarity));

  return (
    <div className="gacha-summary" data-testid="gacha-summary">
      <h3>RESULTADO</h3>
      <ul className="gacha-grid">
        {results.map((result, position) => (
          <li
            key={result.instanceId}
            data-rarity={result.rarity}
            data-intensity={revealIntensity(result.rarity)}
            data-transform={hasTransformation(result.rarity) ? 'true' : 'false'}
            data-best={position === best ? 'true' : 'false'}
            style={rarityStyle(result.rarity)}
          >
            {/* Clicking looks. It does not equip, activate or change anything. */}
            <button
              type="button"
              className="gacha-grid-card"
              data-testid={`gacha-card-${result.instanceId}`}
              onClick={() => onInspect(result)}
            >
              <Art manifest={manifest} result={result} size="grid" />
              <span className="market-row-name">{result.name}</span>
              <span className="market-row-sub">
                {rarityLabel(result.rarity)} · {result.levelLabel}
                {result.stars ? ` · ${'★'.repeat(result.stars)}` : ''}
              </span>
              {result.forcedByPity ? <span className="gacha-forced">GARANTIA</span> : null}
            </button>
            {position === best ? (
              <span className="gacha-best" data-testid="gacha-best">
                MELHOR
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
